import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Compass, Loader2, Plus, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "sightseeing", label: "Sightseeing" },
  { value: "tour", label: "Tour" },
  { value: "activity", label: "Activity" },
];

const emptyItem = {
  id: "",
  name: "",
  category: "sightseeing",
  description: "",
  timing_hint: "",
  active: true,
  display_order: 0,
};

export default function Explore() {
  const { eventId, currentEvent, refreshEvents } = useOutletContext();
  const [intro, setIntro] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIntro, setSavingIntro] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [editIdx, setEditIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setIntro(data.explore_intro || "");
      setItems(data.activity_options || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const persist = async (nextItems, nextIntro = intro) => {
    const payload = {
      explore_intro: nextIntro,
      activity_options: nextItems.map((a, i) => ({
        ...a,
        display_order: i,
        name: (a.name || "").trim(),
        category: a.category || "sightseeing",
        description: a.description || "",
        timing_hint: a.timing_hint || "",
        active: a.active !== false,
      })),
    };
    const { data } = await api.patch(`/events/${eventId}/explore`, payload);
    setItems(data.activity_options || []);
    setIntro(data.explore_intro || "");
    await refreshEvents?.();
    return data;
  };

  const saveIntro = async () => {
    setSavingIntro(true);
    try {
      await persist(items, intro);
      toast.success("Explore intro saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSavingIntro(false);
    }
  };

  const openNew = () => {
    setForm({ ...emptyItem, category: "sightseeing" });
    setEditIdx(null);
    setOpen(true);
  };

  const openEdit = (item, idx) => {
    setForm({ ...emptyItem, ...item });
    setEditIdx(idx);
    setOpen(true);
  };

  const saveItem = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const next = [...items];
      const entry = {
        ...form,
        name: form.name.trim(),
        id: form.id || "",
      };
      if (editIdx !== null) next[editIdx] = entry;
      else next.push(entry);
      await persist(next);
      toast.success(editIdx !== null ? "Updated" : "Added to catalog");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (idx) => {
    if (!window.confirm("Remove this from the catalog?")) return;
    try {
      const next = items.filter((_, i) => i !== idx);
      await persist(next);
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    }
  };

  const toggleActive = async (idx, active) => {
    const next = items.map((a, i) => (i === idx ? { ...a, active } : a));
    setItems(next);
    try {
      await persist(next);
    } catch (e) {
      toast.error("Could not update");
      load();
    }
  };

  const filtered = items.filter((a) => {
    if (filter === "all") return true;
    if (filter === "inactive") return a.active === false;
    return (a.category || "sightseeing") === filter;
  });

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="explore-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Explore catalog</h1>
          <p className="text-sm text-[#525252]">
            Sightseeing, tours, and activities for {currentEvent?.name || "this event"}
          </p>
          <p className="mt-1 text-xs text-[#737373]">
            Guests mark interest on RSVP (not a booking). The dashboard lists who asked for what so you can pass it to concierge.
          </p>
        </div>
        <Button onClick={openNew} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="add-explore-item">
          <Plus className="mr-2 h-4 w-4" /> Add item
        </Button>
      </div>

      <div className="rounded-md border border-[#E5E5E5] bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#525252]">Intro on invite</h2>
          <Button size="sm" onClick={saveIntro} disabled={savingIntro} className="bg-[#1A362D] hover:bg-[#12271f]">
            {savingIntro ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Save intro</>}
          </Button>
        </div>
        <RichTextEditor
          key={`explore-intro-${eventId}`}
          value={intro}
          onChange={setIntro}
          minHeight="100px"
          data-testid="explore-intro-editor"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "sightseeing", "tour", "activity", "inactive"].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            className={filter === f ? "bg-[#1A362D]" : ""}
            onClick={() => setFilter(f)}
          >
            {f}
          </Button>
        ))}
      </div>

      <div className="rounded-md border border-[#E5E5E5] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Timing</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((item) => {
              const idx = items.indexOf(item);
              return (
                <TableRow key={item.id || idx}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{item.category || "sightseeing"}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-[#525252]">{item.timing_hint || "—"}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-sm text-[#525252]">
                    {item.description || "—"}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={item.active !== false}
                      onCheckedChange={(v) => toggleActive(idx, v)}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(item, idx)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeItem(idx)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-[#78716C]">
                  <Compass className="mx-auto mb-2 h-7 w-7 opacity-40" />
                  No catalog items yet. Add sightseeing, tours, or activities guests can request.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editIdx !== null ? "Edit catalog item" : "Add catalog item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Amber Fort visit"
                data-testid="explore-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category || "sightseeing"} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="explore-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Timing hint</Label>
                <Input
                  value={form.timing_hint || ""}
                  onChange={(e) => setForm({ ...form, timing_hint: e.target.value })}
                  placeholder="Morning before Sangeet"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Private guide, 3 hours, includes transfers"
              />
            </div>
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <span className="text-sm">Active (shown to guests)</span>
              <Switch
                checked={form.active !== false}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={saveItem} disabled={saving} className="bg-[#1A362D]" data-testid="save-explore-item">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
